import { useState } from 'react'
import { Sparkles, Loader2 } from 'lucide-react'
import { personaWorkshopApi } from '@/lib/api/client'
import { useAIStore } from '@/stores/aiStore'

interface Props {
  accountId: string
  projectId: string
  sessionId: string
  phaseId: string
  label?: string
  onAccept: (text: string) => void
}

export function AiSuggestionChips({ accountId, projectId, sessionId, phaseId, label, onAccept }: Props) {
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { apiKey } = useAIStore()

  const loadSuggestions = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await personaWorkshopApi.suggest(accountId, projectId, sessionId, {
        phaseId,
        apiKey: apiKey || '',
      })
      const items = result?.suggestions?.map((s: { text: string } | string) =>
        typeof s === 'string' ? s : s.text
      ) || result?.archetypes || result?.quotes || []
      setSuggestions(items)
      setHasLoaded(true)
    } catch (err) {
      setError('Failed to load suggestions')
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }

  if (!hasLoaded && !isLoading) {
    return (
      <button
        onClick={loadSuggestions}
        title={label || 'AI suggestions'}
        aria-label={label || 'AI suggestions'}
        className="group mt-1 inline-flex items-center rounded-full px-1.5 py-1 text-[11px] font-medium text-text-muted transition-all duration-150 hover:gap-1.5 hover:bg-warm-100 hover:pr-2.5 hover:text-text-secondary focus-visible:gap-1.5 focus-visible:bg-warm-100 focus-visible:pr-2.5 focus-visible:text-text-secondary focus-visible:outline-none"
      >
        <Sparkles className="h-3 w-3 shrink-0" />
        <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 transition-all duration-150 group-hover:max-w-[220px] group-hover:opacity-100 group-focus-visible:max-w-[220px] group-focus-visible:opacity-100">
          {label || 'AI suggestions'}
        </span>
      </button>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-1 text-[12px] text-text-muted mt-1">
        <Loader2 className="w-3 h-3 animate-spin" /> Generating suggestions...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[11px] text-error">{error}</span>
        <button onClick={loadSuggestions} className="text-[11px] text-market hover:text-market-dark">Retry</button>
      </div>
    )
  }

  if (suggestions.length === 0) {
    return (
      <div className="flex items-center gap-2 mt-1">
        <span className="text-[11px] text-text-muted">No suggestions available</span>
        <button onClick={loadSuggestions} className="text-[11px] text-market hover:text-market-dark">Retry</button>
      </div>
    )
  }

  return (
    <div className="mt-2">
      <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1 flex items-center gap-1">
        <Sparkles className="w-3 h-3 text-market" /> AI suggestions
      </div>
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => {
              onAccept(s)
              setSuggestions(suggestions.filter((item) => item !== s))
            }}
            className="text-[11px] px-2.5 py-1 rounded-full bg-success-bg text-success-fg hover:bg-market-200 transition-colors"
          >
            + {s.length > 60 ? s.substring(0, 60) + '...' : s}
          </button>
        ))}
      </div>
      <button onClick={loadSuggestions} className="text-[10px] text-text-muted hover:text-market mt-1">Refresh</button>
    </div>
  )
}
